import { useEffect, useRef, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { legalDocumentsApi, ApiError } from '@/lib/pillarApi';
import { sessionSensitiveAuthApi } from '@/lib/sessionSensitiveAuthApi';
import {
    invalidateSessionSensitiveRequests,
    StaleEffectiveSessionResponseError,
} from '@/lib/sessionSensitiveRequestManager';
import { buildRequiredTotpSetupPath } from '@/lib/requiredTotpSetup';
import {
    buildCloverOAuthLoginPath,
    parseCloverOAuthLaunchContext,
} from '@/lib/clover/oauth-launch-context';
import { runInBackground } from '@/lib/utils';
import {
    useAuthStore,
    type EffectiveSessionGeneration } from '@/store/auth';
import { createComponentLogger } from '@/lib/logging/frontend-logger';
import {
    ActiveRoleAssignmentResponse,
    ImpersonationContextResponse,
    SessionUserProfileDetailedResponse,
    SessionUserProfileDetailedResponseSchema,
    ListPendingRequiredLegalDocumentsResponse,
    ListPendingRequiredLegalDocumentsResponseSchema,
} from '@anchorbase/pillar-sdk';
import { Status } from '@anchorbase/pillar-sdk';

interface ProtectedRouteProps {
    children: React.ReactNode;
    requireAuth?: boolean;
}

const PUBLIC_EXACT_ROUTES = [
    '/login',
    '/login/password',
    '/login/passwordless',
    '/login/totp',
    '/login/mfa-setup',
    '/forgot-password',
];

const PUBLIC_ROUTE_PREFIXES = ['/reset-password/', '/verify-email/'];

export function ProtectedRoute({ children, requireAuth = true }: ProtectedRouteProps) {
    const log = createComponentLogger('ProtectedRoute');
    const navigate = useNavigate();
    const location = useLocation();
    const pathname = location.pathname;
    const loginPath: string =
        pathname === '/clover/launch'
            ? (() => {
                  const context = parseCloverOAuthLaunchContext(
                      new URLSearchParams(location.search)
                  );
                  return context === null ? '/login' : buildCloverOAuthLoginPath(context);
              })()
            : '/login';
    const queryClient = useQueryClient();

    const {
        user,
        setUserForEffectiveSessionGeneration,
        getEffectiveSessionGeneration,
        logout: logoutStore,
        isLoggingOut,
        setIsLoggingOut,
    } = useAuthStore();
    const [isMounted, setIsMounted] = useState(false);
    const profileGenerationRef = useRef<EffectiveSessionGeneration>(
        getEffectiveSessionGeneration()
    );

    const isPublicRoute = pathname
        ? PUBLIC_EXACT_ROUTES.includes(pathname) ||
          PUBLIC_ROUTE_PREFIXES.some((route) => pathname.startsWith(route))
        : false;
    const requiresPasswordChange: boolean = user?.requires_password_change === true;
    const isPasswordChangeRoute: boolean = pathname.startsWith('/profile/password');

    useEffect(() => {
        setIsMounted(true);
    }, []);

    // Cleanup the isLoggingOut flag when navigating to a public route
    useEffect(() => {
        if (isPublicRoute && isLoggingOut) {
            setIsLoggingOut(false);
        }
    }, [isPublicRoute, isLoggingOut, setIsLoggingOut]);

    // Cancel and reset profile query
    // Only remove queries on public routes or when explicitly logging out
    // Don't remove queries when !user on protected routes - that's normal state before profile fetch completes
    useEffect(() => {
        if (isPublicRoute || isLoggingOut) {
            runInBackground(queryClient.cancelQueries({ queryKey: ['profile'] }), (err) => {
                log.error('Error canceling queries', err);
            });
            runInBackground(queryClient.removeQueries({ queryKey: ['profile'] }), (err) => {
                log.error('Error removing queries', err);
            });
        }
    }, [isPublicRoute, isLoggingOut, queryClient]);

    const isQueryEnabled = requireAuth && isMounted && !isPublicRoute && !user && !isLoggingOut;

    const {
        data: profileData,
        error,
        isLoading,
        isFetched, // Used to track if the profile check has ever completed
    } = useQuery<SessionUserProfileDetailedResponse | null>({
        queryKey: ['profile'],
        queryFn: async () => {
            const response = await sessionSensitiveAuthApi.getProfile({
                handle401AsSessionExpired: false,
            });
            if (response === null || response === undefined) {
                return null;
            }
            profileGenerationRef.current = getEffectiveSessionGeneration();
            return SessionUserProfileDetailedResponseSchema.parse(response);
        },
        retry: false,
        enabled: isQueryEnabled,
        staleTime: 0,
        gcTime: 0,
        refetchOnWindowFocus: false,
        refetchOnMount: true, // Always refetch on mount to check auth status
        refetchOnReconnect: false,
    });

    useEffect(() => {
        if (profileData) {
            setUserForEffectiveSessionGeneration(
                profileData,
                profileGenerationRef.current
            );
        }
    }, [profileData, setUserForEffectiveSessionGeneration]);

    const isActiveRoleAssignmentQueryEnabled: boolean =
        requireAuth &&
        isMounted &&
        !isPublicRoute &&
        !!user &&
        !requiresPasswordChange &&
        !isLoggingOut;

    const {
        data: activeRoleAssignment,
        isLoading: isActiveRoleAssignmentLoading,
        isFetched: isActiveRoleAssignmentFetched,
    } = useQuery<ActiveRoleAssignmentResponse | null>({
        queryKey: ['activeRoleAssignment'],
        queryFn: async () => {
            return await sessionSensitiveAuthApi.getActiveRoleAssignment();
        },
        retry: false,
        enabled: isActiveRoleAssignmentQueryEnabled,
        staleTime: 0,
        gcTime: 0,
        refetchOnWindowFocus: false,
        refetchOnMount: true,
        refetchOnReconnect: false,
    });

    const {
        data: impersonationContext,
        isLoading: isImpersonationContextLoading,
        isFetched: isImpersonationContextFetched,
    } = useQuery<ImpersonationContextResponse | null>({
        queryKey: ['impersonationContext'],
        queryFn: async () => {
            return await sessionSensitiveAuthApi.getImpersonationContext();
        },
        retry: false,
        enabled: isActiveRoleAssignmentQueryEnabled,
        staleTime: 0,
        gcTime: 0,
        refetchOnWindowFocus: false,
        refetchOnMount: true,
        refetchOnReconnect: false,
    });

    const isPendingLegalDocsQueryEnabled: boolean =
        requireAuth &&
        isMounted &&
        !isPublicRoute &&
        !!user &&
        !requiresPasswordChange &&
        !isLoggingOut;

    const {
        data: pendingRequiredLegalDocuments,
        error: pendingRequiredLegalDocumentsError,
        isLoading: isPendingRequiredLegalDocumentsLoading,
        isFetched: isPendingRequiredLegalDocumentsFetched,
    } = useQuery<ListPendingRequiredLegalDocumentsResponse>({
        queryKey: ['pendingRequiredLegalDocuments'],
        queryFn: async () => {
            const response = await legalDocumentsApi.listPendingRequiredLegalDocuments();
            return ListPendingRequiredLegalDocumentsResponseSchema.parse(response);
        },
        retry: false,
        enabled: isPendingLegalDocsQueryEnabled,
        staleTime: 0,
        gcTime: 0,
        refetchOnWindowFocus: false,
        refetchOnMount: true,
        refetchOnReconnect: false,
    });

    const activePendingLegalDocuments: ListPendingRequiredLegalDocumentsResponse | null =
        pendingRequiredLegalDocuments === undefined
            ? null
            : {
                  legal_documents: pendingRequiredLegalDocuments.legal_documents.filter(
                      (document) => document.status === Status.ACTIVE
                  ),
              };

    useEffect(() => {
        if (!requireAuth || isPublicRoute || isLoggingOut) {
            return;
        }
        if (!requiresPasswordChange) {
            return;
        }
        if (isPasswordChangeRoute) {
            return;
        }
        runInBackground(navigate('/profile/password', { replace: true }), (err) => {
            log.error('Error navigating to password change', err);
        });
    }, [
        requireAuth,
        isPublicRoute,
        isLoggingOut,
        requiresPasswordChange,
        isPasswordChangeRoute,
        navigate,
        log,
    ]);

    useEffect(() => {
        if (!requireAuth || isPublicRoute || isLoggingOut) {
            return;
        }
        if (requiresPasswordChange) {
            return;
        }
        if (!user) {
            return;
        }
        if (!isPendingRequiredLegalDocumentsFetched || isPendingRequiredLegalDocumentsLoading) {
            return;
        }
        if (activePendingLegalDocuments === null) {
            return;
        }
        if (pathname.startsWith('/legal-workflow')) {
            return;
        }
        if (activePendingLegalDocuments.legal_documents.length === 0) {
            return;
        }
        const returnTo: string = encodeURIComponent(pathname);
        runInBackground(navigate(`/legal-workflow?returnTo=${returnTo}`, { replace: true }), (err) => {
            log.error('Error navigating to legal workflow', err);
        });
    }, [
        requireAuth,
        isPublicRoute,
        isLoggingOut,
        requiresPasswordChange,
        user,
        isPendingRequiredLegalDocumentsFetched,
        isPendingRequiredLegalDocumentsLoading,
        activePendingLegalDocuments,
        pathname,
        navigate,
        log,
    ]);

    useEffect(() => {
        if (pendingRequiredLegalDocumentsError === null) {
            return;
        }
        log.error('Pending legal documents query failed', pendingRequiredLegalDocumentsError);
    }, [pendingRequiredLegalDocumentsError, log]);

    const hasConfiguredMfaMethod: boolean =
        user?.totp_enabled === true ||
        user?.mfa_email_allowed === true ||
        user?.mfa_sms_allowed === true;
    const requiresMandatoryTotpSetup: boolean =
        impersonationContext === null
        && !hasConfiguredMfaMethod
        && activeRoleAssignment?.combined_config.org_config.platformConfig?.require2FA === true;

    const resolvedActiveRoleAssignment: ActiveRoleAssignmentResponse | null =
        activeRoleAssignment ?? null;

    useEffect(() => {
        if (!requireAuth || isPublicRoute || isLoggingOut) {
            return;
        }
        if (requiresPasswordChange) {
            return;
        }
        if (!user) {
            return;
        }
        if (!isActiveRoleAssignmentFetched || isActiveRoleAssignmentLoading) {
            return;
        }
        if (!isImpersonationContextFetched || isImpersonationContextLoading) {
            return;
        }
        if (!requiresMandatoryTotpSetup) {
            return;
        }
        if (pathname.startsWith('/login/mfa-setup')) {
            return;
        }
        runInBackground(
            navigate(
                buildRequiredTotpSetupPath(
                    resolvedActiveRoleAssignment === null
                        ? undefined
                        : {
                              role_assignment_uuid: resolvedActiveRoleAssignment.role_assignment_uuid,
                              organization_uuid: resolvedActiveRoleAssignment.organization_uuid,
                              organization_name: resolvedActiveRoleAssignment.organization_name,
                          }
                ),
                { replace: true }
            ),
            (err) => {
                log.error('Error navigating to mandatory TOTP setup', err);
            }
        );
    }, [
        requireAuth,
        isPublicRoute,
        isLoggingOut,
        requiresPasswordChange,
        user,
        isActiveRoleAssignmentFetched,
        isActiveRoleAssignmentLoading,
        isImpersonationContextFetched,
        isImpersonationContextLoading,
        requiresMandatoryTotpSetup,
        pathname,
        resolvedActiveRoleAssignment,
        navigate,
        log,
    ]);

    // Handle invalid session: 401 Unauthorized
    useEffect(() => {
        if (
            requireAuth &&
            !isPublicRoute &&
            pathname !== '/login' &&
            error &&
            error instanceof ApiError &&
            error.status === 401
        ) {
            setIsLoggingOut(true);
            runInBackground(queryClient.clear(), (err) => {
                log.error('Error clearing queries', err);
            });
            runInBackground(queryClient.removeQueries({ queryKey: ['profile'] }), (err) => {
                log.error('Error removing queries', err);
            });
            logoutStore();
            invalidateSessionSensitiveRequests();
            runInBackground(navigate(loginPath, { replace: true }), (err) => {
                log.error('Error navigating to login', err);
            });
        }
    }, [
        error,
        requireAuth,
        isPublicRoute,
        pathname,
        logoutStore,
        navigate,
        queryClient,
        setIsLoggingOut,
        loginPath,
    ]);

    useEffect(() => {
        // Only redirect if:
        // 1. Query is/was enabled (we're on a protected route and user is null)
        // 2. Query has completed (isFetched is true AND not currently loading)
        // 3. No profile data (query failed or returned no data)
        // 4. No user was set (profile fetch failed or returned no user)
        // 5. Not currently logging out
        // 6. No 401/403 error (those are handled by the error handler above)
        const shouldRedirectToLogin =
            requireAuth &&
            !isPublicRoute &&
            !user &&
            !profileData &&
            !isLoggingOut &&
            isQueryEnabled &&
            isFetched &&
            !isLoading &&
            !(error && error instanceof ApiError && error.status === 401) &&
            !(error instanceof StaleEffectiveSessionResponseError);

        if (shouldRedirectToLogin) {
            runInBackground(navigate(loginPath, { replace: true }), (err) => {
                log.error('Error navigating to login', err);
            });
        }
    }, [
        requireAuth,
        isPublicRoute,
        user,
        profileData,
        isLoggingOut,
        isQueryEnabled,
        isFetched,
        isLoading,
        error,
        navigate,
        loginPath,
    ]);

    // --- RENDERING LOGIC ---

    // Don't block rendering on public routes (render immediately)
    if (isPublicRoute) {
        return <>{children}</>;
    }

    // Show loading state if auth is required and we are waiting for initial mount OR fetch
    if (
        requireAuth
        && (
            !isMounted
            || isLoading
            || isPendingRequiredLegalDocumentsLoading
            || isActiveRoleAssignmentLoading
            || isImpersonationContextLoading
        )
    ) {
        return (
            <div className="min-h-screen flex items-center justify-center">
                <p>Loading...</p>
            </div>
        );
    }

    if (
        requireAuth &&
        !isPublicRoute &&
        user &&
        requiresPasswordChange &&
        !isPasswordChangeRoute
    ) {
        return (
            <div className="min-h-screen flex items-center justify-center">
                <p>Redirecting to password change...</p>
            </div>
        );
    }

    if (
        requireAuth &&
        !isPublicRoute &&
        user &&
        requiresMandatoryTotpSetup &&
        !pathname.startsWith('/login/mfa-setup')
    ) {
        return (
            <div className="min-h-screen flex items-center justify-center">
                <p>Redirecting to security settings...</p>
            </div>
        );
    }

    if (
        requireAuth &&
        !isPublicRoute &&
        user &&
        activePendingLegalDocuments !== null &&
        activePendingLegalDocuments.legal_documents.length > 0 &&
        !pathname.startsWith('/legal-workflow')
    ) {
        return (
            <div className="min-h-screen flex items-center justify-center">
                <p>Redirecting to legal workflow...</p>
            </div>
        );
    }

    // If we've passed all checks (auth required, loading done, user is present), render children
    return <>{children}</>;
}
